import { opportunityDocumentsService } from '@/services/opportunity-documents.service';
import axios from 'axios';

const initialState = () => ({
  // matchedKeywordsTerms: [],
  matchInformation: [],
  matchedKwByDocument: [],
  matchesByKeyword: [],
  searchResult: [],
  status: {
    error: null,
    pending: false,
    resolved: false,
    rejected: false,
  },
});

const state = initialState();

const CancelToken = axios.CancelToken;
let cancelTokens = {
  opportunityMatchInformation: null,
};

const actions = {
  getOpportunityMatchInformation({ commit, rootState }, { opportunityId }) {
    if (cancelTokens.opportunityMatchInformation) {
      cancelTokens.opportunityMatchInformation.cancel('Multiple requests.');
    }
    cancelTokens.opportunityMatchInformation = CancelToken.source();

    commit('GET_OPPORTUNITY_MATCHES');

    return opportunityDocumentsService
      .getOpportunityPdfEngineMatchInformation({ opportunityId, cancelToken: cancelTokens.opportunityMatchInformation })
      .then(
        (matchInformation) => {
          commit('GET_OPPORTUNITY_MATCHES_SUCCESS', {
            matchInformation,
            categories: rootState.documentKeywords.categories,
          });
        },
        (error) => {
          if (!axios.isCancel(error)) {
            commit('GET_OPPORTUNITY_MATCHES_ERROR', error);
            throw error;
          }
        },
      );
  },
  searchContent({ commit }, { opportunityId, query, cancelToken }) {
    commit('SEARCH_CONTENT');
    return opportunityDocumentsService.searchContent({ opportunityId, query, cancelToken }).then(
      (results) => {
        commit('SEARCH_CONTENT_SUCCESS', results);
      },
      (error) => {
        if (!axios.isCancel(error)) {
          commit('SEARCH_CONTENT_ERROR', error);
          throw error;
        }
      },
    );
  },
};

const mutations = {
  GET_OPPORTUNITY_MATCHES(state) {
    state.status = {
      error: null,
      pending: true,
      resolved: false,
      rejected: false,
    };
    // state.matchedKeywordsTerms = [];
    state.matchInformation = [];
    state.matchedKwByDocument = [];
    state.matchesByKeyword = [];
  },
  /**
   * <ol>
   *  <li> categories is the tenant keyword categories. Each category has a list of defined keywords. </li>
   *  <li> matchInformation is the occurrence (page# and word#) of the categories' keywords list in the documents. </li>
   *  <li> state.matchedKwByDocument is the list of keywords by document. It is a list containing the keyword, the document it is found in, then pages it occurs in the document, and the number of occurrences in that document. </li>
   *  <li> matchedKwByOpp is the list of ALL keywords (not by document) and their total number of occurrences in ALL documents (iow, in the opportunity). </li>
   *  <li> state.matchInformation is the list of categories augmented with the category's keywords and its total number of occurrences in the opportunity </li>
   *  <li> keywordsTextContents is the list of keywords (and their category) and their occurrences (page and word index) in the documents. </li>
   *  <li> state.matchedKeywordsTerms is the list of keywords with all their declinations. E.g., for jeu~, it will contain jeu, jeux, etc. </li>
   * </ol>
   */
  GET_OPPORTUNITY_MATCHES_SUCCESS(state, { matchInformation, categories }) {
    state.status = {
      ...state.status,
      pending: false,
      resolved: true,
    };

    state.matchesByKeyword = matchInformation;

    // For each document of the opportunity, find all the matched keywords and their occurrences
    matchInformation.forEach((matchInformation) => {
      const keyword = matchInformation.keyword;
      const matchesByDoc = matchInformation.matchesByDoc;
      Object.entries(matchesByDoc).forEach(([docId, matches]) => {
        state.matchedKwByDocument.push({
          docId,
          keyword,
          occurrences: matches.length,
          pages: matches.map((match) => match.start.page),
        });
      });
    });

    // Merge duplicates from keywords found on multiple documents
    const matchedKwByOpp = Object.values(
      state.matchedKwByDocument.reduce((acc, obj) => {
        const { id } = obj.keyword;
        const occurrences = obj.occurrences;

        // Use id to verify duplicates
        acc[id] = acc[id] || { keyword: obj.keyword, occurrences: 0 };

        // Adds occurrences
        acc[id].occurrences += occurrences;

        return acc;
      }, {}),
    );

    // Then, build the object that'll be use by `keywordNavigation`
    state.matchInformation = categories.map((category) => ({
      categoryId: category.id,
      name: category.name,
      colorCode: category.colorCode,
      results: matchedKwByOpp.filter((match) => match.keyword.categoryId === category.id && match.occurrences > 0),
    }));
  },
  GET_OPPORTUNITY_MATCHES_ERROR(state, error) {
    state.status = {
      ...state.status,
      error: error,
      pending: false,
      rejected: true,
    };
  },
  SEARCH_CONTENT(state) {
    state.status = {
      error: null,
      pending: true,
      resolved: false,
      rejected: false,
    };
    state.searchResult = [];
  },
  SEARCH_CONTENT_SUCCESS(state, results) {
    state.status = {
      ...state.status,
      pending: false,
      resolved: true,
    };
    const matches = results && results.length > 0 ? results[0].matches : [];
    // Deduplicate identical spans — pdf-engine can return the same span
    // multiple times for long queries with gaps (backtracking finds multiple paths).
    state.searchResult = matches.map((match) => ({
      ...match,
      spans: match.spans.filter(
        (span, i, arr) =>
          arr.findIndex(
            (s) =>
              s.start.page === span.start.page &&
              s.start.element === span.start.element &&
              s.start.offset === span.start.offset &&
              s.end.element === span.end.element &&
              s.end.offset === span.end.offset,
          ) === i,
      ),
    }));
  },
  SEARCH_CONTENT_ERROR(state, error) {
    state.status = {
      ...state.status,
      error,
      pending: false,
      rejected: true,
    };
  },
};

const getters = {
  // matchedKeywordsTerms: (state) => state.matchedKeywordsTerms,
  matchedKwByDocument: (state) => state.matchedKwByDocument,
  matchInformation: (state) => state.matchInformation,
  shownMatchInformation: (state) => state.matchInformation.flatMap((category) => category.results || []),
  matchesByKeyword: (state) => state.matchesByKeyword,
  searchResult: (state) => state.searchResult,

  // status
  error: (state) => state.status.error,
  pending: (state) => state.status.pending,
  rejected: (state) => state.status.rejected,
  resolved: (state) => state.status.resolved,
};

export const opportunityDocumentsPdfEngine = {
  namespaced: true,
  state,
  actions,
  mutations,
  getters,
};
